import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSearch, FaUser, FaBell, FaBars, FaTimes, FaHome, FaInfoCircle, FaCogs, FaEnvelope, FaSignInAlt, FaUserPlus } from 'react-icons/fa';
import { Dropdown } from 'react-bootstrap';
import AuthForms from './AuthForms';
import './Header.css';

// Import logo
const logo = '../assets/gsk.jpg'; // Adjust the path as necessary

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { path: '/', label: 'Home', icon: <FaHome /> },
    { path: '/about', label: 'About', icon: <FaInfoCircle /> },
    { path: '/services', label: 'Services', icon: <FaCogs /> },
    { path: '/contact', label: 'Contact', icon: <FaEnvelope /> }
  ];

  // Sample search data - replace with your actual data
  const searchData = [
    { id: 1, title: 'Web Development', type: 'course', path: '/courses/web-development' },
    { id: 2, title: 'Data Science', type: 'course', path: '/courses/data-science' },
    { id: 3, title: 'Mobile App Development', type: 'course', path: '/courses/mobile-development' },
    { id: 4, title: 'Python Programming', type: 'course', path: '/courses/python' },
    { id: 5, title: 'Machine Learning', type: 'course', path: '/courses/machine-learning' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      const results = searchData.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchResultClick = (result) => {
    navigate(result.path);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchOpen(false);
  };

  const handleAuthClick = () => {
    setShowAuthModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={toggleMenu}>
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          
          <div className="logo">
            <Link to="/">
              <img src={logo} alt="School Logo" className="logo-image" />
            </Link>
          </div>
        </div>

        <div className="header-nav">
          {menuItems.map((item) => (
            <Link 
              key={item.path}
              to={item.path}
              className={`header-nav-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </div>

        <div className="header-center">
          <div className={`search-container ${isSearchOpen ? 'active' : ''}`} ref={searchRef}>
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search courses, projects, and more..."
                value={searchQuery}
                onChange={handleSearch}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  ×
                </button>
              )}
            </div>
            
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map(result => (
                  <div
                    key={result.id}
                    className="search-result-item"
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <FaSearch className="result-icon" />
                    <div className="result-content">
                      <span className="result-title">{result.title}</span>
                      <span className="result-type">{result.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="header-right">
          <button className="search-toggle" onClick={toggleSearch}>
            <FaSearch />
          </button>
          
          <div className="header-actions">
            <button className="notification-btn">
              <FaBell />
              <span className="notification-badge">3</span>
            </button>
            
            <Dropdown align="end">
              <Dropdown.Toggle as="div" className="profile-btn">
                <FaUser />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={handleAuthClick}>
                  <FaSignInAlt className="me-2" /> Login
                </Dropdown.Item>
                <Dropdown.Item onClick={handleAuthClick}>
                  <FaUserPlus className="me-2" /> Sign Up
                </Dropdown.Item>
                <Dropdown.Divider />
                {/* <Dropdown.Item as={Link} to="/profile">
                  <FaUser className="me-2" /> My Profile
                </Dropdown.Item> */}
                <Dropdown.Item as={Link} to="/settings">
                  <FaCogs className="me-2" /> Settings
                </Dropdown.Item>
                <Dropdown.Divider />
                {/* <Dropdown.Item as={Link} to="/logout">
                  Logout
                </Dropdown.Item> */}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <nav className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          <ul>
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link 
                  to={item.path} 
                  onClick={() => setIsMenuOpen(false)}
                  className={location.pathname === item.path ? 'active' : ''}
                >
                  <span className="menu-icon">{item.icon}</span>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <AuthForms show={showAuthModal} handleClose={() => setShowAuthModal(false)} />
    </header>
  );
};

export default Header; 